import React, { useMemo, useState } from "react";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import "../../styles/console.css";

const INTEL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "portfolio", label: "City Portfolio" },
  { id: "doctrine", label: "Doctrine Compliance" },
  { id: "runners", label: "Runners & Activation" },
];

function KpiCard({ label, value, sub, subWarn = false }) {
  return (
    <div className="intel-kpi-card">
      <div className="intel-kpi-label">{label}</div>
      <div className="intel-kpi-value">{value}</div>
      {sub ? <div className={`intel-kpi-sub ${subWarn ? "warn" : ""}`}>{sub}</div> : null}
    </div>
  );
}

function SimpleBars({ rows = [], valueKey = "count", labelKey = "label" }) {
  const max = Math.max(1, ...rows.map((r) => Number(r?.[valueKey]) || 0));
  return (
    <div className="intel-bars">
      {rows.map((row) => {
        const value = Number(row?.[valueKey]) || 0;
        const pct = Math.round((value / max) * 100);
        return (
          <div key={`${row?.[labelKey]}-${value}`} className="intel-bar-row">
            <div className="intel-bar-label">{row?.[labelKey]}</div>
            <div className="intel-bar-track">
              <div className="intel-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="intel-bar-value">{value}</div>
          </div>
        );
      })}
    </div>
  );
}

function IntelligenceFlagsGrid({ flags = [], keyPrefix = "flag" }) {
  return (
    <div className="intel-flags-grid">
      {flags.map((flag, idx) => (
        <div key={`${keyPrefix}-${flag.title}-${idx}`} className={`intel-flag-card sev-${flag.severity || "info"}`}>
          <div className="intel-flag-title">{flag.title}</div>
          <div className="intel-flag-body">{flag.body}</div>
        </div>
      ))}
    </div>
  );
}

export default function IntelligenceView() {
  const [activeTab, setActiveTab] = useState("overview");
  const { data, loading, error } = useConsoleFetch(
    () => consoleApi.stats.networkIntelligence({ range_key: "next_7_days" }),
    []
  );

  const monthlyUsers = useMemo(
    () => (Array.isArray(data?.growth_series?.users_by_month) ? data.growth_series.users_by_month : []),
    [data?.growth_series?.users_by_month]
  );
  const monthlyMeetings = useMemo(
    () =>
      Array.isArray(data?.growth_series?.meetings_by_month)
        ? data.growth_series.meetings_by_month
        : [],
    [data?.growth_series?.meetings_by_month]
  );

  const summary = data?.summary_kpis ?? {};
  const doctrineRows = Array.isArray(data?.doctrine_compliance?.rows) ? data.doctrine_compliance.rows : [];
  const cityCards = Array.isArray(data?.portfolio?.cities) ? data.portfolio.cities : [];
  const intelFlags = Array.isArray(data?.intelligence_flags) ? data.intelligence_flags : [];
  const topCities = Array.isArray(data?.distribution_breakdowns?.top_cities)
    ? data.distribution_breakdowns.top_cities
    : [];
  const authProviders = data?.distribution_breakdowns?.auth_providers ?? {};
  const authRows = useMemo(
    () => [
      { label: "Email/password", count: Number(authProviders.email) || 0 },
      { label: "Apple", count: Number(authProviders.apple) || 0 },
      { label: "Google", count: Number(authProviders.google) || 0 },
      { label: "Both", count: Number(authProviders.both) || 0 },
    ],
    [authProviders.apple, authProviders.both, authProviders.email, authProviders.google]
  );
  const portfolioSummary = useMemo(() => {
    const list = Array.isArray(cityCards) ? cityCards : [];
    const citiesOnboarded = list.length;
    const citiesAtDoctrineRuns = list.filter((c) => Number(c?.recurring_runs) >= 50).length;
    const totalRecurringRuns = list.reduce((sum, c) => sum + (Number(c?.recurring_runs) || 0), 0);
    const stableCities = list.filter(
      (c) => String(c?.status || "").toLowerCase() === "stable"
    ).length;
    return { citiesOnboarded, citiesAtDoctrineRuns, totalRecurringRuns, stableCities };
  }, [cityCards]);
  const funnelDropoff = useMemo(() => {
    const setup = Number(data?.activation_funnel?.setup_complete) || 0;
    const publicCount = Number(data?.activation_funnel?.public_profile) || 0;
    const stuck = Math.max(0, setup - publicCount);
    const stuckPct = setup > 0 ? Math.round((stuck / setup) * 100) : 0;
    return { setup, publicCount, stuck, stuckPct };
  }, [data?.activation_funnel?.public_profile, data?.activation_funnel?.setup_complete]);

  const overviewDerived = useMemo(() => {
    const tu = Number(summary.total_users) || 0;
    const rn = Number(summary.runners) || 0;
    const rc = Number(summary.run_clubs) || 0;
    const pp = Number(summary.public_profiles) || 0;
    const cm = Number(summary.club_memberships) || 0;
    const pe = Number(summary.push_enabled) || 0;
    const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : null);
    let monthDelta = null;
    if (monthlyUsers.length >= 2) {
      const last = Number(monthlyUsers[monthlyUsers.length - 1]?.count) || 0;
      const prev = Number(monthlyUsers[monthlyUsers.length - 2]?.count) || 0;
      monthDelta = last - prev;
    }
    const pr = pct(pp, rn);
    const pTu = pct(pe, tu);
    const runnersPct = pct(rn, tu);
    const clubsPct = pct(rc, tu);
    return {
      userMonthSub: monthDelta == null ? null : `${monthDelta >= 0 ? "+" : ""}${monthDelta} vs prior month`,
      runnersPctSub: runnersPct != null ? `${runnersPct}% of users` : null,
      clubsPctSub: clubsPct != null ? `${clubsPct}% of users` : null,
      publicSub: pr != null ? `${pr}% of runners` : null,
      publicWarn: pr != null && pr < 35,
      clubMemAvgSub: rn > 0 && cm > 0 ? `Avg ${(cm / rn).toFixed(1)} per runner` : null,
      pushSub: pTu != null ? `${pTu}% of users` : null,
      pushWarn: tu > 0 && pe / tu < 0.2,
    };
  }, [monthlyUsers, summary]);

  return (
    <ConsoleViewShell loading={loading} error={error} loadingLabel="Loading intelligence dashboard…">
      {!data ? (
        <div className="console-view">
          <p className="console-muted-text">No intelligence data available.</p>
        </div>
      ) : (
        <div className="console-main-inner console-intelligence">
          <ConsolePageHeader
            label="RunCards Network"
            title="Intelligence Dashboard"
            sub="Live operator intelligence across users, onboarding doctrine, and city portfolio health."
          />

          <div className="intel-tab-nav" role="tablist" aria-label="Intelligence sections">
            {INTEL_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`intel-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "overview" && (
            <>
              <div className="section-label">User Base</div>
              <div className="intel-kpi-grid">
                <KpiCard label="Total Users" value={summary.total_users ?? 0} sub={overviewDerived.userMonthSub} />
                <KpiCard label="Runners" value={summary.runners ?? 0} sub={overviewDerived.runnersPctSub} />
                <KpiCard label="Run Clubs" value={summary.run_clubs ?? 0} sub={overviewDerived.clubsPctSub} />
                <KpiCard
                  label="Public Profiles"
                  value={summary.public_profiles ?? 0}
                  sub={overviewDerived.publicSub}
                  subWarn={overviewDerived.publicWarn}
                />
              </div>

              <div className="section-label">Activity & Engagement</div>
              <div className="intel-kpi-grid">
                <KpiCard label="Meetings Scheduled" value={summary.meetings_scheduled ?? 0} />
                <KpiCard
                  label="Club Memberships"
                  value={summary.club_memberships ?? 0}
                  sub={overviewDerived.clubMemAvgSub}
                />
                <KpiCard label="Run Registrations" value={summary.run_registrations ?? 0} />
                <KpiCard
                  label="Push Enabled"
                  value={summary.push_enabled ?? 0}
                  sub={overviewDerived.pushSub}
                  subWarn={overviewDerived.pushWarn}
                />
              </div>

              <div className="intel-two-col">
                <div className="intel-card">
                  <div className="section-label">User Growth by Month</div>
                  <SimpleBars rows={monthlyUsers} />
                </div>
                <div className="intel-card">
                  <div className="section-label">Meetings by Month</div>
                  <SimpleBars rows={monthlyMeetings} />
                </div>
              </div>

              <div className="section-label">Key Observations</div>
              <IntelligenceFlagsGrid flags={intelFlags} keyPrefix="overview" />
            </>
          )}

          {activeTab === "portfolio" && (
            <>
              <div className="section-label">Portfolio Overview</div>
              <div className="intel-kpi-grid">
                <KpiCard label="Cities Onboarded" value={portfolioSummary.citiesOnboarded} />
                <KpiCard label="Cities at 50+ Runs" value={portfolioSummary.citiesAtDoctrineRuns} />
                <KpiCard label="Total Recurring Runs" value={portfolioSummary.totalRecurringRuns} />
                <KpiCard label="Stable Cities" value={portfolioSummary.stableCities} sub="Flagship markets (phase stable)" />
              </div>
              <p className="intel-portfolio-quote">
                &quot;Can a runner open RunCards in this city and reliably find a good run any day of the week — AM or
                PM?&quot;
              </p>
              <div className="section-label">City Portfolio</div>
              <div className="intel-city-list">
                {cityCards.map((city) => (
                  <div key={city.city} className="intel-city-card">
                    <div className="intel-city-header">
                      <div className="intel-city-name">{city.city}</div>
                      <span className={`intel-phase-pill phase-${String(city.status || "").toLowerCase()}`}>
                        {city.status}
                      </span>
                    </div>
                    <div className="intel-city-meta">
                      <span>{city.recurring_runs} recurring runs</span>
                      <span>{city.anchor_clubs} anchor clubs</span>
                      <span>{city.covered_slots}/14 weekly slots</span>
                    </div>
                    <div className="intel-slot-grid">
                      {Object.entries(city.coverage_cells || {}).map(([key, val]) => (
                        <div key={key} className="intel-slot-cell">
                          <div className="intel-slot-key">{key}</div>
                          <div className="intel-slot-val">{val}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === "doctrine" && (
            <>
              <div className="section-label">Doctrine Compliance — All Cities</div>
              <div className="intel-table-wrap">
                <table className="intel-table">
                  <thead>
                    <tr>
                      <th>City</th>
                      <th>50+ Runs</th>
                      <th>8/14 Cells</th>
                      <th>Photos</th>
                      <th>4–6 Anchors</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctrineRows.map((row) => (
                      <tr key={row.city}>
                        <td>{row.city}</td>
                        <td className={row.runs_pass ? "pass" : "fail"}>{row.runs}</td>
                        <td className={row.cells_pass ? "pass" : "fail"}>
                          {row.cells}/14
                        </td>
                        <td className={row.photos_pass ? "pass" : "fail"}>
                          {row.photos_pass ? "Complete" : "Incomplete"}
                        </td>
                        <td className={row.anchors_pass ? "pass" : "fail"}>{row.anchors}</td>
                        <td>{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="intel-doctrine-intel">
                <div className="section-label">
                  Intelligence Layer — computed from current data, updates as cities are onboarded
                </div>
                <IntelligenceFlagsGrid flags={intelFlags} keyPrefix="doctrine" />
              </div>
            </>
          )}

          {activeTab === "runners" && (
            <>
              <div className="intel-funnel-block">
                <div className="section-label">Activation Funnel</div>
                <SimpleBars
                  rows={[
                    { label: "Signed up", count: data?.activation_funnel?.signed_up ?? 0 },
                    { label: "Setup complete", count: data?.activation_funnel?.setup_complete ?? 0 },
                    { label: "Has runcard", count: data?.activation_funnel?.has_runcard ?? 0 },
                    { label: "Public profile", count: data?.activation_funnel?.public_profile ?? 0 },
                    { label: "Has connection", count: data?.activation_funnel?.has_connection ?? 0 },
                  ]}
                />
                <div className="intel-funnel-alert">
                  <strong>Critical drop-off:</strong> Setup ({funnelDropoff.setup}) -> public profile (
                  {funnelDropoff.publicCount}). {funnelDropoff.stuckPct}% are stuck before public activation.
                </div>
              </div>

              <div className="intel-two-col">
                <div className="intel-card">
                  <div className="section-label">Meetings by City</div>
                  <SimpleBars rows={topCities} valueKey="meetings" labelKey="city" />
                </div>
                <div className="intel-card">
                  <div className="section-label">Auth Provider Breakdown</div>
                  <SimpleBars rows={authRows} />
                </div>
              </div>
              <div className="intel-card intel-card-spaced">
                <div className="section-label">Run Difficulty Distribution</div>
                <SimpleBars
                  rows={Array.isArray(data?.distribution_breakdowns?.run_difficulty) ? data.distribution_breakdowns.run_difficulty : []}
                />
              </div>
            </>
          )}
        </div>
      )}
    </ConsoleViewShell>
  );
}
